/**
 * Buildings: /portal/buildings (RULING R5, Isaiah Cotto, 2026-10-14, "yes":
 * building pages for building managers who are not on the health-data
 * allowlist).
 *
 * The building screens that already live inside Scheduling -- add a building,
 * edit one, the Monday reconciliation -- on a page of their own, for a
 * facility.manage holder at enterprise scope. It calls ONLY the five building
 * routes (/scheduling/buildings*), which the server mounts ahead of the PHI
 * operator allowlist because they return no resident. Nothing here names a
 * resident: the Scheduling board, the facility table and the ATD queue do,
 * so they stay in Scheduling, behind the allowlist, and are not linked from
 * here. The server decides everything; this page only says what it answered.
 *
 * With no list of buildings among those five routes, a building is edited
 * here when it is one just added on this page, or one waiting for adoption
 * in the reconciliation report (as in the report's drawer in Scheduling).
 */
import React, { useEffect, useState } from 'react';
import { Building2 } from 'lucide-react';
import WorkspaceMenu from '../../components/WorkspaceMenu';
import { WorkspaceLayout, SidebarTrigger, type SidebarGroup } from '../../components/workspaceSidebar';
import { outcomeOf } from '../finance/financeApi';
import { AddBuildingButton, EditBuildingSection } from './BuildingEditor';
import { ReconciliationReport } from './BuildingReconciliation';
import { buildingsApi, Building, BuildingMeta } from './buildingsApi';
import '../umed/umed.css';
import '../finance/finance.css';
import './scheduling.css';

// Authorization V2 codes arrive prefixed (AUTHZ_DENY_NO_CAPABILITY); compared by their bare name, as ProviderShell does.
const bareCode = (code: string | undefined | null) => String(code || '').replace(/^AUTHZ_/, '');
// One view: the sidebar names the workspace and holds its one door.
const GROUPS: SidebarGroup[] = [
  { key: 'buildings', label: 'Buildings', items: [{ key: 'buildings', label: 'Buildings', icon: Building2, href: '/portal/buildings', active: true }] },
];
const NOT_A_MANAGER = 'Your account does not manage buildings. Adding and editing buildings needs a ratified Scheduling, Operations or Facility manager role across all buildings; ask your administrator.';

export default function BuildingsWorkspace() {
  const [meta, setMeta] = useState<BuildingMeta | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // Buildings added during this visit: editable here, newest first.
  const [added, setAdded] = useState<Building[]>([]);
  // A new building may change the report (an arrival linked, an item now held): reload it.
  const [round, setRound] = useState(0);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const r = await buildingsApi.meta();
        if (!live) return;
        if (r && r.success && r.data) setMeta(r.data);
        else setRefusal(r && bareCode(r.code) === 'DENY_NO_CAPABILITY' ? NOT_A_MANAGER : outcomeOf(r));
      } catch { if (live) setRefusal('Could not reach the server.'); }
    })();
    return () => { live = false; };
  }, []);

  const card = (body: React.ReactNode) => <main className="um-main one"><section className="um-card"><h2>Buildings</h2>{body}</section></main>;
  let content: React.ReactNode;
  if (refusal) content = card(<p className="um-quiet" role="alert">{refusal}</p>);
  else if (!meta) content = card(<p className="um-quiet" role="status">Opening Buildings…</p>);
  else if (!meta.enterprise) content = card(<p className="um-quiet" role="alert">{meta.why || NOT_A_MANAGER}</p>);
  else {
    content = (
      <main className="um-main one">
        {notice && <p className="um-notice ok" role="status">{notice}</p>}
        <section className="um-card" aria-label="Add a building">
          <h2>Add a building</h2>
          {meta.can.create
            ? <div className="btns"><AddBuildingButton meta={meta} onAdded={(b) => { setAdded((cur) => [b, ...cur.filter((x) => x.id !== b.id)]); setNotice(`${b.name}: added in eMerge.`); setRound((n) => n + 1); }} /></div>
            : <p className="um-quiet">{meta.why || 'Adding buildings is not switched on for your account.'}</p>}
        </section>
        {added.map((b) => (
          <section key={b.id} className="um-card">
            <EditBuildingSection facilityId={b.id} name={b.name} meta={meta} onChanged={(m) => { setNotice(m); setRound((n) => n + 1); }} />
          </section>
        ))}
        <section className="um-card" aria-label="Monday reconciliation">
          <h2>Monday reconciliation</h2>
          <p className="um-quiet">Where Monday and eMerge disagree about a building. eMerge is the authority; nothing here changes Monday or a building by itself.</p>
          <ReconciliationReport key={round} meta={meta} onNotice={setNotice} />
        </section>
      </main>
    );
  }

  return (
    <div className="um">
      <WorkspaceMenu current="BUILDINGS" />
      <WorkspaceLayout workspace="BUILDINGS" navLabel="Buildings views" groups={GROUPS}>
        <header className="um-bar"><div className="um-bar-inner"><div className="um-ident"><div className="wsb-title"><SidebarTrigger /><div className="um-where">
          <span className="um-eyebrow">eMerge · Buildings</span><h1>Buildings</h1>
          <span className="um-sub">Add a building, edit one, and see where Monday differs. No resident is shown here: the Scheduling board, facility table and ATD queue name residents, so they stay in Scheduling for people on the health-data access list.</span>
        </div></div></div></div></header>
        {content}
      </WorkspaceLayout>
    </div>
  );
}
